import { useState, useEffect, useRef } from 'react'
import { EnhancementOption } from 'screens/VirtualMarketerOnboarding/EnhanceBrandFonts/EnhancementOption/EnhancementOption'
import { ColorItem } from './ColorItem/ColorItem'
import Styles from './ColorOption.module.scss'
import { Button } from 'blaze-ui/Button'
import { ArrowRefresh2, Edit1, Plus } from 'blaze-ui/icons'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { Text } from 'blaze-ui/Text/Text'

interface ColorOptionProps {
  colors: string[]
  description?: string[]
  isSelected: boolean
  onSelect: () => void
  ordinal: number
  title: string
  badgeText?: string
  onPersistColors: (newColors: string[]) => void
  previewColors?: string[]
  setPreviewColors?: (colors: string[]) => void
}

export const ColorOption = ({
  colors,
  description,
  isSelected,
  onSelect,
  ordinal,
  title,
  badgeText,
  onPersistColors,
  previewColors,
  setPreviewColors,
}: ColorOptionProps) => {
  const [localColors, setLocalColors] = useState<string[]>(colors)
  const originalColors = useRef<string[]>(colors)

  useEffect(() => {
    setLocalColors(colors)
  }, [colors])

  const hasChanges = JSON.stringify(localColors) !== JSON.stringify(originalColors.current)

  const handleColorChange = (index: number) => (newValue: string) => {
    setLocalColors((prev) => prev.map((c, i) => (i === index ? newValue : c)))
  }

  const persistChanges = () => {
    onPersistColors(localColors)
  }

  const handleDeleteClick = (index: number) => () => {
    const newColors = localColors.filter((_, i) => i !== index)
    setLocalColors(newColors)
    onPersistColors(newColors)
  }

  const resetToOriginal = () => {
    setLocalColors(originalColors.current)
    onPersistColors(originalColors.current)
  }

  const addColor = () => {
    setLocalColors((prev) => [...prev, '#FFFFFF'])
  }

  return (
    <EnhancementOption
      isSelected={isSelected}
      onSelect={onSelect}
      ordinal={ordinal}
      title={title}
      badgeText={badgeText}
      description={description}
    >
      <div className={Styles.colorDisplay}>
        {localColors.map((color, index) => (
          <ColorItem
            key={index}
            color={color}
            index={index}
            onColorChange={handleColorChange}
            onPersistChanges={persistChanges}
            onDeleteClick={handleDeleteClick}
            isDeletable={localColors.length > 1}
            previewColors={previewColors}
            setPreviewColors={setPreviewColors}
          />
        ))}
      </div>
      <div className={Styles.buttons}>
        {hasChanges && (
          <Button onPress={resetToOriginal} variant="ghost" frontIcon={ArrowRefresh2}>
            Reset Colors
          </Button>
        )}
        {localColors.length < 5 && (
          <Button onPress={addColor} variant="ghost" frontIcon={Plus}>
            Add color
          </Button>
        )}
        {localColors.length === 5 && !hasChanges && (
          <ResponsiveDesign.Mobile>
            <div className={Styles.mobileEducation}>
              <Edit1 color="var(--dark-60)" />
              <Text variant="secondary" color="var(--dark-60)">
                Tap a swatch to change the color
              </Text>
            </div>
          </ResponsiveDesign.Mobile>
        )}
      </div>
    </EnhancementOption>
  )
}
